/** SCIM bearer-token manager — Phase 3-scim admin.
 *
 * Org admins use this to mint / rotate / revoke the SCIM token an
 * IdP uses to provision and offboard users. The plaintext value is
 * shown ONCE on issue/rotate — after that only the prefix is visible.
 *
 * Behaviour:
 *   - GET: list metadata about the active token (or 404 → "none").
 *   - POST: mint a new token; on success show a "copy this once"
 *     banner with the plaintext. The cached query is invalidated so
 *     the metadata read reflects the new prefix.
 *   - DELETE: revoke without minting a replacement. SCIM stops
 *     working until the admin issues a new token.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { ScimTokenIssued, ScimTokenMeta } from "@/lib/types";

const SCIM_BASE_URL = `${window.location.origin}/api/scim/v2`;

export function ScimTokenCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [revealed, setRevealed] = useState<string | null>(null);

  const meta = useQuery({
    queryKey: ["scim-token"],
    queryFn: async () => {
      const res = await api.get<ScimTokenMeta>(ApiPaths.orgScimToken);
      return res.data;
    },
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });

  const mint = useMutation({
    mutationFn: async () => {
      const res = await api.post<ScimTokenIssued>(ApiPaths.orgScimToken);
      return res.data;
    },
    onSuccess: (issued) => {
      setRevealed(issued.token);
      qc.invalidateQueries({ queryKey: ["scim-token"] });
    },
  });

  const revoke = useMutation({
    mutationFn: async () => {
      await api.delete(ApiPaths.orgScimToken);
    },
    onSuccess: () => {
      setRevealed(null);
      qc.invalidateQueries({ queryKey: ["scim-token"] });
    },
  });

  const has = !!meta.data;

  return (
    <section className="card space-y-3" data-testid="scim-token-card">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.scim.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">
          <Trans i18nKey="admin.scim.cardHint" components={[<em key="em" />]} />
        </p>
      </header>

      <div className="rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-xs">
        <div className="text-nb-text-2">{t("admin.scim.baseUrlLabel")}</div>
        <code className="break-all text-nb-mint" data-testid="scim-base-url">
          {SCIM_BASE_URL}
        </code>
      </div>

      {revealed && (
        <div
          className="space-y-2 rounded-md border border-cvp-warn bg-cvp-warn/10 px-3 py-2 text-xs text-amber-100"
          role="alert"
          data-testid="scim-token-reveal"
        >
          <p className="font-semibold">{t("admin.scim.copyWarning")}</p>
          <code className="block break-all rounded bg-black/40 px-2 py-1 text-amber-200">
            {revealed}
          </code>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setRevealed(null)}
          >
            {t("admin.scim.iStoredIt")}
          </button>
        </div>
      )}

      <div className="space-y-1 text-sm">
        {meta.isLoading && !meta.isFetched ? (
          <p className="text-nb-text-2">{t("admin.scim.loading")}</p>
        ) : has ? (
          <>
            <p>
              <span className="text-nb-text-2">{t("admin.scim.activeTokenLabel")}</span>{" "}
              <code className="text-nb-text">
                {meta.data!.token_prefix}…
              </code>
            </p>
            <p className="text-xs text-nb-text-3">
              {t("admin.scim.issuedAt", {
                when: formatDateTime(meta.data!.created_at),
              })}
              {meta.data!.last_used_at && (
                <>
                  {t("admin.scim.lastUsedAt", {
                    when: formatDateTime(meta.data!.last_used_at),
                  })}
                </>
              )}
            </p>
          </>
        ) : (
          <p className="text-nb-text-2">{t("admin.scim.noToken")}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => mint.mutate()}
          disabled={mint.isPending}
        >
          {mint.isPending
            ? t("admin.scim.issuing")
            : has
              ? t("admin.scim.rotateButton")
              : t("admin.scim.issueButton")}
        </button>
        {has && (
          <button
            type="button"
            className="btn-danger text-sm"
            onClick={() => {
              if (window.confirm(t("admin.scim.revokeConfirm"))) {
                revoke.mutate();
              }
            }}
            disabled={revoke.isPending}
          >
            {revoke.isPending
              ? t("admin.scim.revoking")
              : t("admin.scim.revokeButton")}
          </button>
        )}
      </div>

      {mint.isError && (
        <p className="text-xs text-cvp-danger" role="alert">
          {t("admin.scim.issueError")}
        </p>
      )}
    </section>
  );
}
